import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  Button,
  Snackbar,
  Alert,
  FormControlLabel,
  Typography,
  Checkbox,
  Box,
} from "@mui/material";
import {
  fonts,
  headingFonts,
  primaryButtonStyles,
  secondaryButtonStyles,
  selectStyles,
  textFieldStyles,
} from "../../../../Styles/Styles";
import ResultModal from "./ResultModal";

export default function EditServiceProviderModal({
  open,
  onClose,
  data,
  onSave,
}) {
  const [allServices, setAllServices] = useState([]);
  const [services, setServices] = useState([]);
  const [subServices, setSubServices] = useState([]);
  const [selectedService, setSelectedService] = useState("");
  const [selectedSubService, setSelectedSubService] = useState([]);
  const [name, setName] = useState(data?.name || "");
  const [businessDescription, setBusinessDescription] = useState(
    data?.description || ""
  );
  const [contactInfo, setContactInfo] = useState(
    data?.contactInfo || { email: "", phone: "", countryCode: "+1345" }
  );
  const [address, setAddress] = useState(data?.address || "");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  // Validation errors
  const [errors, setErrors] = useState({});
  const [resultModal, setResultModal] = useState({
    open: false,
    type: "",
    message: "",
  });

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/services/allservices`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const servicesData = await response.json();

        // Filter services into parent and subservices
        const parentServices = servicesData.filter(
          (service) => service.parent === null
        );
        const allSubServices = servicesData.filter(
          (service) => service.parent !== null
        );
        console.log(servicesData);

        setAllServices(servicesData);
        setServices(parentServices);

        // Only set initial values for selectedService and selectedSubService if not already set
        if (data?.skills?.length > 0 && !selectedService) {
          const serviceSkill = data.skills[0]; // Assuming only one skill is available
          const selectedParentService =
            serviceSkill?.parentServiceId?._id || "";
          const selectedSubServices =
            serviceSkill?.subServiceIds?.map((subService) => subService._id) ||
            [];

          setSelectedService(selectedParentService);
          setSelectedSubService(selectedSubServices);
        }

        // Update the list of subservices when the parent service is selected
        if (selectedService) {
          const relatedSubServices = allSubServices.filter(
            (sub) => sub.parent === selectedService
          );
          setSubServices(relatedSubServices);
        }
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };

    fetchServices();
  }, [data, selectedService]); // Re-run when data or selectedService changes

  // console.log(allSubServices);
  const handleServiceChange = (event) => {
    const selectedServiceId = event.target.value;
    setSelectedService(selectedServiceId);

    const relatedSubservices = allServices.filter(
      (sub) => sub.parent === selectedServiceId
    );
    setSubServices(relatedSubservices);
    setSelectedSubService([]);
  };

  const handleSubServiceChange = (event, subServiceId) => {
    if (event.target.checked) {
      setSelectedSubService((prev) => [...prev, subServiceId]);
    } else {
      setSelectedSubService((prev) => prev.filter((id) => id !== subServiceId));
    }
  };

  useEffect(() => {
    if (open && data) {
      setName(data?.name || "");
      setBusinessDescription(data?.description || "");
      setContactInfo(
        data?.contactInfo || { email: "", phone: "", countryCode: "+1345" }
      );
      setAddress(data?.address || "");
      setSelectedService("");
      setSelectedSubService([]);
    }
  }, [open, data]);

  const validateFields = () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = "Name is required";
    if (!businessDescription.trim())
      newErrors.businessDescription = "Business details are required";
    if (!contactInfo.email.trim()) newErrors.email = "Email is required";
    if (!contactInfo.phone.trim()) newErrors.phone = "Phone number is required";
    if (!address.trim()) newErrors.address = "Address is required";
    if (!selectedService) newErrors.service = "Please select a service";

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateFields()) return;

    try {
      const updatedData = {
        name,
        businessDescription,
        contactInfo,
        address,
        selectedService,
        selectedSubService,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/serviceProviders/update/${data?._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(updatedData),
        }
      );

      if (response.ok) {
        setResultModal({
          open: true,
          type: "success",
          message: "Service provider details updated successfully.",
        });
      } else {
        const errorData = await response.json();
        setResultModal({
          open: true,
          type: "error",
          message: errorData.message || "Failed to update details.",
        });
      }
    } catch (error) {
      console.error("Error updating data:", error);
      setResultModal({
        open: true,
        type: "error",
        message: "An unexpected error occurred. Please try again.",
      });
    } finally {
      onClose();
      // onSave(); // Notify parent component that save was successful
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle sx={{ ...headingFonts }}>
          Edit Service Provider
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Name"
                variant="outlined"
                margin="normal"
                fullWidth
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                sx={{ ...textFieldStyles }}
                error={!!errors.name}
                helperText={errors.name}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Business Details"
                variant="outlined"
                margin="normal"
                fullWidth
                multiline
                rows={4}
                required
                value={businessDescription}
                onChange={(e) => setBusinessDescription(e.target.value)}
                sx={{ ...textFieldStyles }}
                error={!!errors.businessDescription}
                helperText={errors.businessDescription}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl
                fullWidth
                margin="normal"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              >
                <InputLabel sx={{ ...fonts }}>Service</InputLabel>
                <Select
                  value={selectedService}
                  onChange={handleServiceChange}
                  label="Service"
                  required
                  sx={{ ...selectStyles, borderRadius: "8px" }}
                >
                  {services.map((service) => (
                    <MenuItem
                      key={service._id}
                      value={service._id}
                      sx={{ ...fonts }}
                    >
                      {service.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.service && (
                  <Typography color="error" variant="caption">
                    {errors.service}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {subServices.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ ...headingFonts }}>
                  Sub-services
                </Typography>
                {subServices.map((subService) => (
                  <FormControlLabel
                    key={subService._id}
                    margin="normal"
                    control={
                      <Checkbox
                        checked={selectedSubService.includes(subService._id)}
                        onChange={(e) =>
                          handleSubServiceChange(e, subService._id)
                        }
                      />
                    }
                    label={
                      <Typography sx={{ ...fonts }}>
                        {subService.name}
                      </Typography>
                    }
                  />
                ))}
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                label="Email"
                variant="outlined"
                margin="normal"
                fullWidth
                required
                value={contactInfo.email}
                onChange={(e) =>
                  setContactInfo({ ...contactInfo, email: e.target.value })
                }
                sx={{ ...textFieldStyles }}
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>

            <Grid item xs={4}>
              <FormControl
                fullWidth
                margin="normal"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              >
                <InputLabel sx={{ ...fonts }}>Country Code</InputLabel>
                <Select
                  label="Country Code"
                  margin="normal"
                  value={contactInfo.countryCode}
                  onChange={(e) =>
                    setContactInfo({
                      ...contactInfo,
                      countryCode: e.target.value,
                    })
                  }
                  required
                  sx={{ ...selectStyles, borderRadius: "8px" }}
                >
                  <MenuItem value="+1345" sx={{ ...fonts }}>
                    +1345 (Cayman Islands)
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={8}>
              <TextField
                label="Phone"
                variant="outlined"
                margin="normal"
                fullWidth
                required
                value={contactInfo.phone}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d{0,7}$/.test(value)) {
                    setContactInfo({ ...contactInfo, phone: value });
                  }
                }}
                sx={{ ...textFieldStyles }}
                error={!!errors.phone}
                helperText={errors.phone}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Address"
                variant="outlined"
                margin="normal"
                fullWidth
                required
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                sx={{ ...textFieldStyles }}
                error={!!errors.address}
                helperText={errors.address}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sx={{ gap: 2, display: "flex", justifyContent: "flex-end" }}
            >
              <Button
                size="small"
                variant="contained"
                sx={{ ...secondaryButtonStyles }}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                sx={{ ...primaryButtonStyles }}
                onClick={handleSubmit}
              >
                Update
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <ResultModal
        open={resultModal.open}
        type={resultModal.type}
        message={resultModal.message}
        onSave={onSave}
        onClose={() => setResultModal({ ...resultModal, open: false })}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
